import { asyncFrame } from '@1inch-community/core/async';
import { appendStyle, getMobileMatchMediaEmitter, isSafari, isStandalone, resizeObserver, } from '@1inch-community/core/lit-utils';
import { applyColorBrightness, setBrowserMetaColorFilter } from '@1inch-community/core/theme';
import { OverlayViewMode } from '@1inch-community/models';
import { html, render } from 'lit';
import { distinctUntilChanged, filter, fromEvent, map, merge, pairwise, skip, switchMap, takeUntil, tap, } from 'rxjs';
import { ScrollViewProviderElement } from '../scroll';
import { getContainer } from './overlay-container';
import { getOverlayId } from './overlay-id-generator';
import { zIndexMap } from './z-index-map';
const lerp = (min, max, percent) => {
    return min + (max - min) * (percent / 100);
};
const scaleAndTranslate = (scale, offset) => `scale3d(${scale}, ${scale}, ${scale}) translate3d(0, ${offset}%, 0)`;
const blur = (blur) => `blur(${blur}px)`;
const translate = (y) => `translate3d(0, ${y}%, 0)`;
const overlayBorderRadius = (radius) => `${radius}px ${radius}px 0 0`;
const isOverlayNode = (node) => node.id === 'overlay-container' && node instanceof ScrollViewProviderElement;
let touchPositionMap = new WeakMap();
const clearTouchPositionMap = () => {
    touchPositionMap = new WeakMap();
};
export class OverlayMobileController {
    get container() {
        return getContainer();
    }
    constructor(rootNodeName) {
        this.rootNodeName = rootNodeName;
        this.scale = 0.85;
        this.borderRadius = 8;
        this.topOffsetPercent = -5;
        this.overlayBorderRadius = 24;
        this.overlayBackgroundBlur = 1;
        this.activeOverlayMap = new Map();
        this.subscriptions = new Map();
        this.overlayIdStack = [];
    }
    async init() { }
    isOpenOverlay(overlayId) {
        return this.activeOverlayMap.has(overlayId);
    }
    async open(openTarget) {
        clearTouchPositionMap();
        const id = getOverlayId();
        const previousOverlayId = this.findPreviousOverlayId(id);
        const overlayBackground = this.createOverlayBackground(id);
        const overlayContainer = this.createOverlayContainer(id, openTarget);
        const rootNode = this.getRootNodeOrPreviousOverlay(previousOverlayId);
        const previousOverlayBackground = this.getPreviousOverlayBackground(previousOverlayId);
        if (rootNode instanceof ScrollViewProviderElement) {
            rootNode.onChangeStubView(true);
            rootNode.onLockedConsumer(true);
        }
        await asyncFrame(10);
        const fullOverlayView = this.calculateIsFullOverlayView(overlayContainer);
        await this.transition('open', fullOverlayView, overlayContainer, overlayBackground, rootNode, previousOverlayBackground);
        this.applyStyleAfterTransition('open', fullOverlayView, overlayContainer, overlayBackground, rootNode, previousOverlayBackground);
        overlayContainer.takeUpdate();
        this.activeOverlayMap.set(id, [overlayContainer, overlayBackground]);
        this.overlayIdStack.unshift(id);
        this.subscribe(id, overlayContainer, overlayBackground, rootNode, previousOverlayBackground);
        overlayContainer.onChangeStubView(false);
        return id;
    }
    async close(id) {
        if (!this.activeOverlayMap.has(id)) {
            return;
        }
        const previousOverlayId = this.findPreviousOverlayId(id);
        const [overlayContainer, overlayBackground] = this.activeOverlayMap.get(id);
        const rootNode = this.getRootNodeOrPreviousOverlay(previousOverlayId);
        const previousOverlayBackground = this.getPreviousOverlayBackground(previousOverlayId);
        const fullOverlayView = this.calculateIsFullOverlayView(overlayContainer);
        await this.transition('close', fullOverlayView, overlayContainer, overlayBackground, rootNode, previousOverlayBackground);
        this.applyStyleAfterTransition('close', fullOverlayView, overlayContainer, overlayBackground, rootNode, previousOverlayBackground);
        if (rootNode instanceof ScrollViewProviderElement) {
            rootNode.onLockedConsumer(false);
            rootNode.onChangeStubView(false);
        }
        this.unsubscribeOnResize(id);
        this.activeOverlayMap.delete(id);
        this.overlayIdStack.shift();
        overlayContainer.remove();
        overlayBackground.remove();
        clearTouchPositionMap();
    }
    async closeAll() {
        while (this.overlayIdStack.length > 0) {
            const id = this.overlayIdStack[0];
            await this.close(id);
        }
    }
    createOverlayContainer(id, openTarget) {
        const overlayContainer = document.createElement(ScrollViewProviderElement.tagName);
        const overlayIndex = this.activeOverlayMap.size + 1;
        const offsetStep = 5;
        const innerHeight = isSafari() && isStandalone() ? window.innerHeight - 10 : window.innerHeight;
        overlayContainer.onChangeStubView(true);
        overlayContainer.maxHeight = ((100 - overlayIndex * offsetStep) * innerHeight) / 100;
        overlayContainer.id = 'overlay-container';
        overlayContainer.setAttribute('overlay-id', id.toString());
        overlayContainer.setAttribute('overlay-index', overlayIndex.toString());
        const [startKeyframe] = this.getStyleForOverlayContainer('open', overlayContainer);
        const startPosition = startKeyframe;
        const zIndex = zIndexMap[OverlayViewMode.mobile];
        appendStyle(overlayContainer, {
            position: 'fixed',
            display: 'flex',
            width: '100vw',
            overflow: 'hidden',
            alignItems: 'flex-end',
            bottom: '0',
            left: '0',
            zIndex: `${zIndex + id * 10 + 1}`,
            boxSizing: 'border-box',
            borderRadius: overlayBorderRadius(this.overlayBorderRadius),
            ...startPosition,
        });
        if (isSafari() && isStandalone()) {
            appendStyle(overlayContainer, {
                bottom: '10px',
            });
        }
        render(html `${openTarget}`, overlayContainer);
        this.container.appendChild(overlayContainer);
        return overlayContainer;
    }
    createOverlayBackground(id) {
        const overlayBackground = document.createElement('div');
        overlayBackground.id = 'overlay-background';
        overlayBackground.setAttribute('overlay-background-id', id.toString());
        const [start] = this.getStyleForOverlayBackground('open');
        const zIndex = zIndexMap[OverlayViewMode.mobile];
        appendStyle(overlayBackground, {
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.4)',
            zIndex: `${zIndex + id * 10}`,
            ...start,
        });
        this.container.appendChild(overlayBackground);
        return overlayBackground;
    }
    async transition(direction, fullOverlayView, overlayContainer, overlayBackground, rootNodeOrPreviousOverlay, previousOverlayBackground) {
        const animationOptions = this.getDefaultAnimationOptions();
        const overlayContainerStyle = this.getStyleForOverlayContainer(direction, overlayContainer);
        const overlayBackgroundStyle = this.getStyleForOverlayBackground(direction);
        const rootNodeOrPreviousOverlayStyle = this.getStyleForRootNodeOrPreviousOverlay(direction, fullOverlayView, rootNodeOrPreviousOverlay);
        const previousOverlayBackgroundStyle = this.getStyleForPreviousOverlayBackground(direction);
        const resetMetaColor = direction === 'close' && this.activeOverlayMap.size === 1;
        await Promise.all([
            this.updateBrowserMetaColor(fullOverlayView, resetMetaColor),
            overlayContainer.animate(overlayContainerStyle, animationOptions).finished,
            overlayBackground.animate(overlayBackgroundStyle, animationOptions).finished,
            rootNodeOrPreviousOverlay.animate(rootNodeOrPreviousOverlayStyle, animationOptions).finished,
            previousOverlayBackground
                ? previousOverlayBackground.animate(previousOverlayBackgroundStyle, animationOptions)
                    .finished
                : null,
        ]);
    }
    async transitionFullView(direction, fullOverlayView, rootNodeOrPreviousOverlay) {
        const animationOptions = this.getDefaultAnimationOptions();
        const rootNodeOrPreviousOverlayStyle = this.getStyleForRootNodeOrPreviousOverlay(direction, fullOverlayView, rootNodeOrPreviousOverlay);
        await rootNodeOrPreviousOverlay.animate(rootNodeOrPreviousOverlayStyle, animationOptions)
            .finished;
    }
    applyStyleAfterTransition(direction, fullOverlayView, overlayContainer, overlayBackground, rootNodeOrPreviousOverlay, previousOverlayBackground) {
        const [, overlayContainerFinishStyle] = this.getStyleForOverlayContainer(direction, overlayContainer);
        const [, overlayBackgroundFinishStyle] = this.getStyleForOverlayBackground(direction);
        const [, rootNodeOrPreviousOverlayFinishStyle] = this.getStyleForRootNodeOrPreviousOverlay(direction, fullOverlayView, rootNodeOrPreviousOverlay, true);
        appendStyle(overlayContainer, {
            ...overlayContainerFinishStyle,
        });
        appendStyle(overlayBackground, {
            ...overlayBackgroundFinishStyle,
        });
        appendStyle(rootNodeOrPreviousOverlay, {
            ...rootNodeOrPreviousOverlayFinishStyle,
        });
        if (previousOverlayBackground) {
            const [, previousOverlayBackgroundFinishStyle] = this.getStyleForPreviousOverlayBackground(direction);
            appendStyle(previousOverlayBackground, {
                ...previousOverlayBackgroundFinishStyle,
            });
        }
    }
    applyStyleAfterTransitionFullView(direction, fullOverlayView, rootNodeOrPreviousOverlay) {
        const [, rootNodeOrPreviousOverlayFinishStyle] = this.getStyleForRootNodeOrPreviousOverlay(direction, fullOverlayView, rootNodeOrPreviousOverlay, true);
        appendStyle(rootNodeOrPreviousOverlay, {
            ...rootNodeOrPreviousOverlayFinishStyle,
        });
    }
    getStyleForOverlayContainer(direction, node) {
        const { position } = touchPositionMap.get(node) ?? {};
        const start = {
            open: { transform: translate(position ?? 100) },
            close: { transform: translate(position ?? 0) },
        };
        const finish = {
            open: { transform: translate(0) },
            close: { transform: translate(100) },
        };
        return [start[direction], finish[direction]];
    }
    getStyleForOverlayBackground(direction) {
        const start = {
            open: { backdropFilter: blur(0), opacity: '0' },
            close: {
                backdropFilter: blur(this.overlayBackgroundBlur),
                opacity: '1',
            },
        };
        const finish = {
            open: start.close,
            close: start.open,
        };
        return [start[direction], finish[direction]];
    }
    getStyleForRootNodeOrPreviousOverlay(direction, fullOverlayView, node, styleForAppend = false) {
        if (!fullOverlayView) {
            return [{}, {}];
        }
        const { scale, offset } = this.calculateTransform(node);
        const stored = touchPositionMap.get(node) ?? { scale: 1, offset: 0, borderRadius: 0 };
        const isOverlay = isOverlayNode(node);
        const baseTransform = styleForAppend ? '' : scaleAndTranslate(1, 0);
        const baseBorderRadius = isOverlay
            ? overlayBorderRadius(this.overlayBorderRadius)
            : overlayBorderRadius(0);
        const storageTransform = stored.scale && stored.offset ? scaleAndTranslate(stored.scale, stored.offset) : null;
        const storageBorderRadius = stored.borderRadius
            ? overlayBorderRadius(stored.borderRadius)
            : null;
        const start = {
            open: {
                transform: storageTransform ?? baseTransform,
                borderRadius: storageBorderRadius ?? baseBorderRadius,
            },
            close: {
                transform: storageTransform ?? scaleAndTranslate(scale, offset),
                borderRadius: storageBorderRadius ?? `${this.borderRadius}px`,
            },
        };
        const finish = {
            open: {
                transform: scaleAndTranslate(scale, offset),
                borderRadius: `${this.borderRadius}px`,
            },
            close: {
                transform: baseTransform,
                borderRadius: baseBorderRadius,
            },
        };
        return [start[direction], finish[direction]];
    }
    getStyleForPreviousOverlayBackground(direction) {
        const [start, finish] = this.getStyleForOverlayBackground(direction);
        return [finish, start];
    }
    calculateTransform(node) {
        const index = Number(node.getAttribute('overlay-index'));
        return {
            scale: this.scale + index * 0.02,
            offset: this.topOffsetPercent - index * 2,
        };
    }
    getRootNodeOrPreviousOverlay(id) {
        if (id === null) {
            return document.querySelector(this.rootNodeName);
        }
        const frontNode = this.container.querySelector(`#overlay-container[overlay-id="${id}"]`);
        if (!frontNode) {
            return document.querySelector(this.rootNodeName);
        }
        return frontNode;
    }
    getPreviousOverlayBackground(id) {
        if (id === null)
            return null;
        return this.container.querySelector(`#overlay-background[overlay-background-id="${id}"]`);
    }
    findPreviousOverlayId(id) {
        const index = this.overlayIdStack.indexOf(id);
        if (index === -1)
            return this.overlayIdStack[0] ?? null;
        return this.overlayIdStack[index + 1] ?? null;
    }
    subscribe(id, overlayContainer, overlayBackground, rootNodeOrPreviousOverlay, previousOverlayBackground) {
        const closeIfChangeMobileView$ = getMobileMatchMediaEmitter().pipe(skip(1), tap(() => this.close(id)));
        const changeFullView$ = resizeObserver(overlayContainer).pipe(filter(() => this.overlayIdStack[0] === id), map(() => this.calculateIsFullOverlayView(overlayContainer)), distinctUntilChanged(), pairwise(), switchMap(async ([prevFullOverlayView, currFullOverlayView]) => {
            const direction = prevFullOverlayView ? 'close' : 'open';
            const fullOverlayView = prevFullOverlayView ? prevFullOverlayView : currFullOverlayView;
            await this.transitionFullView(direction, fullOverlayView, rootNodeOrPreviousOverlay);
            this.applyStyleAfterTransitionFullView(direction, fullOverlayView, rootNodeOrPreviousOverlay);
        }));
        const closeOnClickBackground$ = fromEvent(overlayBackground, 'click').pipe(tap(() => this.close(id)));
        const touch$ = fromEvent(overlayContainer, 'touchstart', { passive: true }).pipe(filter(() => (overlayContainer.scrollTopFromConsumer ?? 0) === 0), switchMap((startEvent) => {
            const fullView = this.calculateIsFullOverlayView(overlayContainer);
            const { scale, offset } = this.calculateTransform(rootNodeOrPreviousOverlay);
            const overlayContainerHeight = overlayContainer.clientHeight;
            const startPoint = startEvent.touches[0].clientY;
            const swipeMaxForClose = 30;
            let currentDelta = 0;
            let closeReady = false;
            return fromEvent(overlayContainer, 'touchmove', { passive: true }).pipe(filter(() => !closeReady), tap(async (event) => {
                const currentPoint = event.touches[0].clientY;
                const deltaPoint = currentPoint - startPoint;
                const delta = (deltaPoint * 100) / overlayContainerHeight;
                if (delta < 0)
                    return;
                if (delta >= swipeMaxForClose) {
                    closeReady = true;
                    return await this.close(id);
                }
                currentDelta = delta;
                const lerpBlur = lerp(this.overlayBackgroundBlur, 0, delta);
                const lerpOpacity = lerp(1, 0, delta);
                appendStyle(overlayContainer, {
                    transform: translate(delta),
                });
                appendStyle(overlayBackground, {
                    backdropFilter: blur(lerpBlur),
                    opacity: lerpOpacity.toString(),
                });
                touchPositionMap.set(overlayContainer, { position: delta });
                touchPositionMap.set(overlayBackground, { blur: lerpBlur, opacity: lerpOpacity });
                if (previousOverlayBackground) {
                    const lerpBlurPreviousOverlayBackground = lerp(0, this.overlayBackgroundBlur, delta);
                    const lerpOpacityPreviousOverlayBackground = lerp(0, 1, delta);
                    appendStyle(previousOverlayBackground, {
                        backdropFilter: blur(lerpBlurPreviousOverlayBackground),
                        opacity: lerpOpacityPreviousOverlayBackground.toString(),
                    });
                    touchPositionMap.set(previousOverlayBackground, {
                        blur: lerpBlurPreviousOverlayBackground,
                        opacity: lerpOpacityPreviousOverlayBackground,
                    });
                }
                if (fullView) {
                    const isOverlay = isOverlayNode(rootNodeOrPreviousOverlay);
                    const lerpScale = lerp(scale, 1, delta);
                    const lerpOffset = lerp(offset, 0, delta);
                    const lerpBorder = lerp(this.borderRadius, isOverlay ? this.overlayBorderRadius : 0, delta);
                    appendStyle(rootNodeOrPreviousOverlay, {
                        transform: scaleAndTranslate(lerpScale, lerpOffset),
                        borderRadius: overlayBorderRadius(lerpBorder),
                    });
                    touchPositionMap.set(rootNodeOrPreviousOverlay, {
                        scale: lerpScale,
                        offset: lerpOffset,
                        borderRadius: lerpBorder,
                    });
                }
            }), takeUntil(merge(fromEvent(overlayContainer, 'touchend', { passive: true }), fromEvent(overlayContainer, 'touchcancel', { passive: true })).pipe(filter(() => !closeReady), tap(async (endEvent) => {
                if (currentDelta === 0)
                    return;
                if (endEvent.timeStamp - startEvent.timeStamp < 300 &&
                    currentDelta > swipeMaxForClose / 2) {
                    return await this.close(id);
                }
                await this.transition('open', fullView, overlayContainer, overlayBackground, rootNodeOrPreviousOverlay, previousOverlayBackground);
                this.applyStyleAfterTransition('open', fullView, overlayContainer, overlayBackground, rootNodeOrPreviousOverlay, previousOverlayBackground);
                clearTouchPositionMap();
            }))));
        }));
        const subscription = merge(closeIfChangeMobileView$, changeFullView$, closeOnClickBackground$, touch$).subscribe();
        this.subscriptions.set(id, subscription);
    }
    unsubscribeOnResize(overlayId) {
        try {
            if (!this.subscriptions.has(overlayId))
                return;
            const subscription = this.subscriptions.get(overlayId);
            if (subscription.closed)
                return;
            subscription.unsubscribe();
        }
        finally {
            this.subscriptions.delete(overlayId);
        }
    }
    calculateIsFullOverlayView(element) {
        const height = element.clientHeight;
        const maxHeight = element.maxHeight ?? 0;
        return (height * 100) / maxHeight >= 90;
    }
    getDefaultAnimationOptions() {
        return {
            duration: 500,
            easing: 'cubic-bezier(.2, .8, .2, 1)',
        };
    }
    async updateBrowserMetaColor(fullOverlayView, resetColor) {
        appendStyle(document.body, {
            backgroundColor: resetColor ? '' : 'var(--color-background-bg-overlay)',
        });
        await setBrowserMetaColorFilter(resetColor
            ? null
            : (color, isDarkTheme) => {
                if (isDarkTheme && fullOverlayView) {
                    return applyColorBrightness('#ffffff', 0.6);
                }
                if (!isDarkTheme && fullOverlayView) {
                    return applyColorBrightness('#000000', 0.6);
                }
                if (!fullOverlayView) {
                    return applyColorBrightness(color, 0.6);
                }
                return color;
            });
    }
}
//# sourceMappingURL=overlay-mobile-controller.js.map